import React, { useCallback, useMemo } from 'react';
import { Alert, Flex } from '@televet/kibble-ui';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { history, RouteBasePaths, SettingsRoutes } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';

interface IWritebackAlertProps {
  showFailedWritebackAlert: boolean;
}

const WritebackAlerts = ({ showFailedWritebackAlert }: IWritebackAlertProps): JSX.Element | null => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { isComplete, isValid, isClinicWritebackEnabled, isLoading } = usePaymentWritebackConfig();
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();

  const showConfigAlert = useMemo(() => !isLoading && (!isComplete || !isValid), [isLoading, isComplete, isValid]);

  const goToSettings = useCallback(() => {
    history.push(`${RouteBasePaths.Settings}${SettingsRoutes.ClinicPayment}`);
  }, []);

  const configAlertDescription = useMemo(() => {
    return hasAdminRoleAtCurrentClinic
      ? 'Please fix invalid configuration in the payment settings page before retrying a writeback.'
      : 'Please contact an administrator to fix invalid payments configuration before retrying a writeback.';
  }, [hasAdminRoleAtCurrentClinic]);

  return isClinicWritebackEnabled ? (
    <Flex mb={5} gap={5} direction="column">
      {showFailedWritebackAlert && (
        <Alert
          status="info"
          hideCloseButton
          title={`Please verify if failed writebacks were successful in ${primaryIntegrationName}`}
          description="Sometimes timeouts can result in writeback failures in Flow even if the writeback was successful"
        />
      )}
      {showConfigAlert && (
        <Alert
          status="warning"
          hideCloseButton
          title="Invalid configuration could result in failed writebacks"
          description={configAlertDescription}
          buttonProps={
            hasAdminRoleAtCurrentClinic
              ? {
                  buttonText: 'Go to Settings',
                  onClick: goToSettings,
                }
              : undefined
          }
        />
      )}
    </Flex>
  ) : null;
};

export default WritebackAlerts;
